import { sortByIndex } from './node';
import { LayoutDirections, LayoutItemTypes, LayoutSizeType, TYPES } from '../constants';
import { getNodeImageCache } from '../../provider/image';

export function evaluateInputContext(props) {
  const inputContext = {};
  if (props.input) {
    Object.values(props.input).forEach((input) => {
      inputContext[input.id] = {
        id: input.ref,
        label: input.label || '',
        value: input.value,
      };
    });
  }
  processNodes(inputContext, props);
}

function processNode(inputContext, renderNode) {
  if (renderNode.type === TYPES.text) {
    const value = renderNode.text;
    if (value && value.type === 'input') {
      let template = value.value;
      Object.keys(inputContext).forEach((key) => {
        template = template.replace('${' + key + '}', inputContext[key].value);
        if (inputContext[key].id === renderNode.id) {
          renderNode.name = inputContext[key].label;
        }
      });

      renderNode.text = template;
    }
  } else if (renderNode.type === TYPES.image) {
    const value = renderNode.src;
    if (value && value.type === 'input') {
      let template = value.value;
      Object.keys(inputContext).forEach((key) => {
        template = template.replace('${' + key + '}', inputContext[key].value);
        if (inputContext[key].id === renderNode.id) {
          renderNode.name = inputContext[key].label;
        }
      });

      renderNode.src = template;
    }
  }
}

export function processNodes(inputContext, props) {
  if (props.nodes) {
    Object.values(props.nodes).forEach((node) => {
      processNode(inputContext, node);
    });
  }
}

export function layoutNode(renderContext, node) {
  switch (node.type) {
    case TYPES.text:
      return;
    case TYPES.image:
      return layoutImageNode(renderContext, node);
    case TYPES.col:
      return layoutColNode(renderContext, node);
    case TYPES.row:
      return layoutRowNode(renderContext, node);
    default:
      return node;
  }
}

function getNodeBoundBox(renderContext, node) {
  const marginTop = node.marginTop || 0;
  const marginBottom = node.marginBottom || 0;
  const marginLeft = node.marginLeft || 0;
  const marginRight = node.marginRight || 0;

  const paddingTop = node.paddingTop || 0;
  const paddingBottom = node.paddingBottom || 0;
  const paddingLeft = node.paddingLeft || 0;
  const paddingRight = node.paddingRight || 0;

  const imgCache = getNodeImageCache(renderContext, node.id);
  const isEmptyImage = node.type === TYPES.image && !imgCache?.img;

  const h = isEmptyImage ? 0 : node.h;
  const w = isEmptyImage ? 0 : node.w;

  return {
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    h: h + marginTop + marginBottom + paddingTop + paddingBottom,
    w: w + marginLeft + marginRight + paddingLeft + paddingRight,
  };
}

function layoutColNode(renderContext, node) {
  const { h } = node;
  const gap = node.gap || 0;

  if (node.height === LayoutSizeType.hug) {
    const children = renderContext.api.getNodes().filter((n) => n.parentId === node.id);
    children.map((child) => layoutNode(renderContext, child));
    const totalHeight = children.reduce(
      (i, child) => i + getNodeBoundBox(renderContext, child).h + gap,
      0
    );
    node.h = totalHeight;
  }

  if (node.width === LayoutSizeType.hug) {
    const children = renderContext.api.getNodes().filter((n) => n.parentId === node.id);
    children.map((child) => layoutNode(renderContext, child));
    let maxWidth = 0;
    children.forEach((child) => {
      maxWidth = Math.max(getNodeBoundBox(renderContext, child).w, maxWidth);
    });
    node.w = maxWidth;
  }

  if (node.width === LayoutSizeType.fill) {
    const parentNode = renderContext.api.getNode(node.parentId);
    const box = getNodeBoundBox(renderContext, node);
    if (parentNode) {
      const parentBox = getNodeBoundBox(renderContext, parentNode);
      const parentWidth = parentNode.w - parentBox.paddingLeft - parentBox.paddingRight;
      node.w = parentWidth - box.marginLeft - box.marginRight - node.x;
    } else {
      node.w = renderContext.card.props.w - box.marginLeft - box.marginRight - node.x;
    }
  }

  if (node.height === LayoutSizeType.fill) {
    const parentNode = renderContext.api.getNode(node.parentId);
    const box = getNodeBoundBox(renderContext, node);
    if (parentNode) {
      const parentBox = getNodeBoundBox(renderContext, parentNode);
      const parentHeight = parentNode.h - parentBox.paddingTop - parentBox.paddingBottom;
      node.h = parentHeight - box.marginTop - box.marginBottom - node.y;
    } else {
      node.h = renderContext.card.props.h - box.marginTop - box.marginBottom - node.y;
    }
  }

  const children = renderContext.api
    .getNodes()
    .filter((n) => n.parentId === node.id)
    .sort(sortByIndex);
  children.map((child) => layoutNode(renderContext, child));
  const childrenHeight = children.reduce((i, n) => i + getNodeBoundBox(renderContext, n).h, 0);
  let startY = 0;

  if (node.direction === 'bottom') {
    startY = h - childrenHeight - gap * (children.length - 1);
  } else if (node.direction === 'center') {
    startY = (h - childrenHeight - gap * (children.length - 1)) / 2;
  }

  const items = node.items || LayoutItemTypes.start;
  if (items === LayoutItemTypes.start) {
    children.forEach((child) => {
      const box = getNodeBoundBox(renderContext, child);
      child.x = box.marginLeft;
      child.y = startY + box.marginTop;
      startY += box.h + gap;
    });
  } else if (items === LayoutItemTypes.center) {
    children.forEach((child) => {
      const box = getNodeBoundBox(renderContext, child);
      child.x = (node.w - box.w) / 2 + box.marginLeft;
      child.y = startY + box.marginTop;
      startY += box.h + gap;
    });
  } else if (items === LayoutItemTypes.end) {
    children.forEach((child) => {
      const box = getNodeBoundBox(renderContext, child);
      child.x = node.w - box.w - box.marginRight;
      child.y = startY + box.marginTop;
      startY += box.h + gap;
    });
  } else {
    children.forEach((child) => {
      const box = getNodeBoundBox(renderContext, child);
      child.x = box.marginLeft;
      child.y = startY + box.marginTop;
      startY += box.h + gap;
    });
  }

  return node;
}

function layoutRowNode(renderContext, node) {
  const children = renderContext.api
    .getNodes()
    .filter((n) => n.parentId === node.id)
    .sort(sortByIndex);

  children.map((child) => layoutNode(renderContext, child));

  if (node.width === 'Hug') {
    const totalWidth = children.reduce((i, child) => i + child.w, 0);
    node.w = totalWidth;
  }
  if (node.height === 'Hug') {
    let maxHeight = 0;
    children.forEach((child) => {
      maxHeight = Math.max(child.h, maxHeight);
    });
    node.h = maxHeight;
  }

  let gap = node.gap || 0;

  const padding = node.padding || 0;
  const paddingX = node.paddingX ?? padding;
  const paddingY = node.paddingY ?? padding;

  const w = node.w - paddingX * 2;
  const x = paddingX;
  const y = paddingY;
  const h = node.h - paddingY * 2;

  const childrenWidth = children.reduce((i, n) => i + getNodeBoundBox(renderContext, n).w, 0);
  let startX = x;
  const startY = y;

  if (node.justify === 'space-between') {
    const totalChildrenWidth = children.reduce(
      (sum, child) => sum + getNodeBoundBox(renderContext, child).w,
      0
    );
    const remainingSpace = node.w - totalChildrenWidth - 2 * paddingX;
    const newGap = remainingSpace / (children.length - 1);
    gap = newGap;
  }

  if (node.direction === LayoutDirections.right) {
    startX = x + w - childrenWidth - gap * (children.length - 1);
  } else if (node.direction === LayoutDirections.center) {
    startX = x + (w - childrenWidth - gap * (children.length - 1)) / 2;
  }

  const items = node.items || LayoutItemTypes.start;
  children.forEach((child) => {
    const box = getNodeBoundBox(renderContext, child);
    child.x = startX + box.marginLeft;
    child.y = startY + box.marginTop;

    if (items === LayoutItemTypes.center) {
      child.y = startY + (h - box.h) / 2 + box.marginTop;
    } else if (items === LayoutItemTypes.end) {
      child.y = startY + (h - box.h) + box.marginTop;
    }

    startX += child.w + gap + box.marginRight;
  });

  return node;
}

function layoutImageNode(renderContext, node) {
  return node;
}
